<template>
    <BaseCard theme="muted" padding="lg" class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
            <h3 class="title-body text-ink">{{ t("reflections.results.awaitingHeading") }}</h3>
            <p class="body-small text-ink-muted">
                <template v-if="totalInvited > 0">{{ t("reflections.results.awaitingTally", { responded: respondedCount, invited: totalInvited }) }} </template>
                {{ t("reflections.results.awaitingBody") }}
            </p>
        </div>
        <div class="flex flex-col items-start gap-2">
            <BaseButton type="button" theme="primary" size="sm" class="whitespace-nowrap" @click="emit('complete')">
                {{ t("reflections.results.awaitingComplete") }}
            </BaseButton>
            <!-- Deliberately a quiet text action, not a second button: the
                 report is better with the reflection, so the way out is
                 visible but not inviting. The confirm dialog still gates it. -->
            <button v-if="canGenerateWithout" type="button" class="waive-link" :disabled="generating" @click="confirmOpen = true">
                {{ t("reflections.results.awaitingGenerateWithout") }}
            </button>
        </div>
        <BaseNotification v-if="error" theme="error" :text="error" @dismiss="error = null" />

        <BaseDialog v-model:open="confirmOpen" :title="t('reflections.results.awaitingGenerateWithoutTitle')" dialog-content-class="md:max-w-[440px]">
            <div class="flex flex-col gap-6 px-10 pt-10 pb-2 md:pb-10">
                <div class="flex flex-col gap-2">
                    <h2 class="display-serif text-ink">{{ t("reflections.results.awaitingGenerateWithoutTitle") }}</h2>
                    <p class="body text-ink-muted">{{ t("reflections.results.awaitingGenerateWithoutBody") }}</p>
                </div>
                <div class="flex flex-col gap-1">
                    <BaseButton type="button" theme="primary" class="w-full justify-center" :disabled="generating" @click="onConfirm">
                        {{ generating ? t("reflections.results.awaitingGenerateWithoutBusy") : t("reflections.results.awaitingGenerateWithoutConfirm") }}
                    </BaseButton>
                    <BaseButton type="button" theme="tertiary" class="w-full justify-center" @click="confirmOpen = false">
                        {{ t("reflections.results.awaitingGenerateWithoutCancel") }}
                    </BaseButton>
                </div>
            </div>
        </BaseDialog>
    </BaseCard>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { cycleId, canGenerateWithout, respondedCount, totalInvited } = defineProps<{
    cycleId: string;
    canGenerateWithout: boolean;
    respondedCount: number;
    totalInvited: number;
}>();
const emit = defineEmits<{
    (event: "complete"): void;
    // The report is queued; the parent starts polling for it.
    (event: "generated"): void;
}>();

const { t } = useI18n();
const confirmOpen = ref(false);
const generating = ref(false);
const error = ref<string | null>(null);

async function onConfirm() {
    if (generating.value) {
        return;
    }
    generating.value = true;
    error.value = null;
    const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}/generate-without-self-reflection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    }).json();
    generating.value = false;
    confirmOpen.value = false;
    if (fetchError.value) {
        error.value = t("reflections.results.awaitingGenerateWithoutFailed");
        return;
    }
    emit("generated");
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .waive-link {
        @apply details text-ink-muted underline underline-offset-2 hover:text-ink disabled:opacity-60 disabled:no-underline;
    }
}
</style>
